import { useRef } from "react";
import { motion, useScroll, useTransform, useMotionValue, useSpring } from "framer-motion";
import { WifiMark } from "./WifiMark";

const EASE = [0.16, 1, 0.3, 1];

const MaskedLine = ({ children, delay, className = "" }) => (
    <div className="overflow-hidden">
        <motion.div
            initial={{ y: "115%" }}
            animate={{ y: "0%" }}
            transition={{ delay, duration: 1.1, ease: EASE }}
            className={className}
        >
            {children}
        </motion.div>
    </div>
);

export const Hero = ({
    overline = "Rich clothing co. — Est. 2026",
    tagline = "Disconnect to reconnect. Luxury for the unreachable.",
    coords = "51.5072° N / 0.1276° W",
    coordsNote = "No signal beyond this point",
}) => {
    const ref = useRef(null);
    const mx = useMotionValue(0);
    const my = useMotionValue(0);
    const rotateX = useSpring(useTransform(my, [-0.5, 0.5], [7, -7]), { stiffness: 60, damping: 18 });
    const rotateY = useSpring(useTransform(mx, [-0.5, 0.5], [-9, 9]), { stiffness: 60, damping: 18 });

    const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
    const yDrift = useTransform(scrollYProgress, [0, 1], [0, 220]);
    const fade = useTransform(scrollYProgress, [0, 0.8], [1, 0]);
    const bgDrift = useTransform(scrollYProgress, [0, 1], [0, -120]);

    const onMove = (e) => {
        const r = ref.current.getBoundingClientRect();
        mx.set((e.clientX - r.left) / r.width - 0.5);
        my.set((e.clientY - r.top) / r.height - 0.5);
    };

    return (
        <section
            id="top"
            ref={ref}
            onMouseMove={onMove}
            className="relative min-h-screen flex flex-col justify-end overflow-hidden px-6 sm:px-12 pb-10"
            data-testid="hero-section"
        >
            <motion.div style={{ y: bgDrift }} className="absolute inset-0 pointer-events-none">
                <div className="absolute left-1/2 top-[38%] -translate-x-1/2 -translate-y-1/2 w-[80vw] h-[80vw] max-w-[900px] max-h-[900px] rounded-full bg-[radial-gradient(circle,_rgba(255,51,51,0.07)_0%,_rgba(39,39,42,0.25)_40%,_transparent_70%)]" />
                <div className="absolute inset-0 opacity-[0.35] bg-[linear-gradient(to_right,_rgba(63,63,70,0.12)_1px,_transparent_1px),linear-gradient(to_bottom,_rgba(63,63,70,0.12)_1px,_transparent_1px)] bg-[size:12vw_12vw]" />
            </motion.div>

            <div className="absolute top-24 right-6 sm:right-12 text-right" data-testid="hero-coordinates">
                <MaskedLine delay={1.5}>
                    <p className="text-[10px] tracking-[0.3em] uppercase text-zinc-500">51.5072° N / 0.1276° W</p>
                </MaskedLine>
                <MaskedLine delay={1.65}>
                    <p className="text-[10px] tracking-[0.3em] uppercase text-zinc-500 mt-1">No signal beyond this point</p>
                </MaskedLine>
            </div>

            <motion.div style={{ y: yDrift, opacity: fade, rotateX, rotateY, transformPerspective: 1200 }} className="relative z-10">
                <MaskedLine delay={0.3}>
                    <p className="text-[11px] sm:text-xs tracking-[0.45em] uppercase text-zinc-400 mb-4 sm:mb-6" data-testid="hero-overline">
                        Rich clothing co. — Est. 2026
                    </p>
                </MaskedLine>

                <h1
                    className="font-display font-semibold uppercase leading-[0.82] tracking-[-0.04em] text-[clamp(4.5rem,17.5vw,19rem)] whitespace-nowrap"
                    data-testid="hero-word"
                >
                    <MaskedLine delay={0.55}>
                        <span className="inline-flex items-baseline">
                            <WifiMark className="h-[0.6em] w-auto self-center translate-y-[0.05em] mr-[0.01em]" />
                            <span>FFLINE</span>
                        </span>
                    </MaskedLine>
                </h1>

                <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-6 mt-6 sm:mt-8">
                    <MaskedLine delay={0.85}>
                        <p className="font-serif italic text-xl sm:text-2xl lg:text-3xl text-zinc-300 max-w-md" data-testid="hero-tagline">
                            Disconnect to reconnect. Luxury for the unreachable.
                        </p>
                    </MaskedLine>
                    <MaskedLine delay={1.0}>
                        <a
                            href="#collection"
                            onClick={(e) => { e.preventDefault(); document.querySelector("#collection")?.scrollIntoView({ behavior: "smooth" }); }}
                            data-testid="hero-cta-button"
                            className="group inline-flex items-center gap-4 text-[11px] tracking-[0.3em] uppercase text-white"
                        >
                            <span className="w-12 h-12 rounded-full border border-zinc-700 flex items-center justify-center transition-colors duration-500 group-hover:bg-signal group-hover:border-signal">
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="transition-transform duration-500 group-hover:translate-y-0.5">
                                    <path d="M12 4v16m0 0l-6-6m6 6l6-6" />
                                </svg>
                            </span>
                            Enter the void
                        </a>
                    </MaskedLine>
                </div>
            </motion.div>
        </section>
    );
};
